import React from 'react';
import { useTranslation } from 'react-i18next';
import { SectionList, SectionListData } from 'react-native';

import EmptyStateNoSearch from '@components/EmptyState/EmptyStateNoSearch';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import { SkeletonList } from '@components/shared/skeletons';
import { SearchableResult } from '@graphql/generated';
import { Search } from '@root/types';
import { MIN_SEARCH_CHARS } from '@src/constants/constants';
import theme from '@themes/theme';

type GlobalSearchList = {
  title: string;
  totalCount: number;
  data: Search<SearchableResult>[];
  render: (item: Search<SearchableResult>, index?: number) => JSX.Element;
  itemSeparatorComponent?: JSX.Element;
  headerLeft?: JSX.Element;
  sectionFooter?: JSX.Element;
  sectionHeaderMarginBottom?: keyof typeof theme.spacing;
  // Shows a "View all" link in this section's header while totalCount > 5.
  // The caller expands the list and clears this once pressed (TA-11072).
  onViewAll?: () => void;
  // Injected via lists.map((list, index) => ({ ...list, index })) below —
  // declared here so renderSectionHeader's section type matches what
  // SectionList is actually given at runtime.
  index?: number;
};

type GlobalSearchListProps = {
  lists: GlobalSearchList[];
  loading: boolean;
  isViewAllContacts: boolean;
  onPress: () => void;
  isChatsSearch?: boolean;
  search?: string;
  hasError?: boolean;
  onEndReached?: (info: { distanceFromEnd: number }) => void;
  // Renders one skeleton row (TA-10278). When provided, 3 skeleton rows
  // replace the result list while a search is in flight, instead of the
  // footer spinner — matches the shared TA-10089 list-loading pattern.
  // Omitted by any caller that hasn't adopted skeletons yet, which keeps
  // the pre-existing spinner behavior (all current callers — Projects,
  // Tasks, Files, Contacts, and Chats searches — now pass this).
  skeletonRow?: (index: number) => React.ReactElement;
};

const GlobalSearchList = ({
  lists,
  loading,
  onPress,
  isViewAllContacts,
  isChatsSearch = false,
  search,
  hasError = false,
  onEndReached,
  skeletonRow,
}: GlobalSearchListProps) => {
  const { t } = useTranslation('shared');
  const allCountsZero = lists.every(
    (list) => list.totalCount === 0 && list.data.length === 0
  );
  const trimmedSearch = (search ?? '').trim();
  const isBelowThreshold =
    trimmedSearch.length > 0 && trimmedSearch.length < MIN_SEARCH_CHARS;
  // renderSectionHeader runs once per section — only one section (the
  // "anchor") should ever render the type-more/no-results/error state, or it
  // repeats once per section on surfaces with multiple sections (e.g. Chats'
  // Contacts/moreOnTaskTag/Chat history). Prefer the first non-Contacts
  // section as the anchor; fall back to index 0 when every section is
  // Contacts (e.g. the Global Search Contacts tab), since findIndex would
  // otherwise return -1 and no section would ever match.
  const firstNonContactsIndex = lists.findIndex(
    (list) => list.title !== 'Contacts'
  );
  const anchorSectionIndex =
    firstNonContactsIndex === -1 ? 0 : firstNonContactsIndex;
  const renderHeader = ({
    section: {
      sectionHeaderMarginBottom,
      title,
      totalCount,
      headerLeft,
      onViewAll,
      index,
    },
  }: {
    section: SectionListData<Search<SearchableResult>, GlobalSearchList>;
  }) => {
    if (isChatsSearch && !trimmedSearch && !loading) {
      return <Box />;
    }

    const isAnchorSection = index === anchorSectionIndex;

    if (isBelowThreshold) {
      return isAnchorSection ? (
        <EmptyStateNoSearch variant='typeMore' showSmall={!isChatsSearch} />
      ) : null;
    }

    if (allCountsZero && !loading && !trimmedSearch) {
      return isAnchorSection ? <EmptyStateNoSearch /> : null;
    }

    return (
      <>
        <Box
          paddingVertical='xs'
          paddingHorizontal={!isChatsSearch ? 'l' : 'm'}
          justifyContent='space-between'
          flexDirection='row'
          alignItems='center'
          marginBottom={sectionHeaderMarginBottom || undefined}>
          {headerLeft && (
            <Box flex={1} alignItems='flex-start'>
              {headerLeft}
            </Box>
          )}
          {!allCountsZero &&
            totalCount > 5 &&
            title === 'Contacts' &&
            isViewAllContacts && (
              <Text onPress={onPress} variant='labelSmall'>
                {t('viewAll')}
              </Text>
            )}
          {onViewAll && totalCount > 5 && (
            <Text onPress={onViewAll} variant='labelSmall'>
              {t('viewAll')}
            </Text>
          )}
        </Box>
        {isAnchorSection &&
          allCountsZero &&
          !loading &&
          trimmedSearch &&
          hasError && (
            <Box padding='m' alignItems='center'>
              <Text variant='body' color='grey04' textAlign='center'>
                {t('searchTemporarilyUnavailable')}
              </Text>
            </Box>
          )}
        {isAnchorSection &&
          allCountsZero &&
          !loading &&
          trimmedSearch &&
          !hasError && <EmptyStateNoSearch showSmall={!isChatsSearch} />}
      </>
    );
  };

  const renderFooter = () => {
    if (!loading) return undefined;
    // Never show a skeleton below the character threshold, or for a blank
    // search — neither has a real search in flight for the current term, so
    // `loading` (if true) reflects a stale in-flight request the debounce is
    // about to abort or supersede.
    if (skeletonRow && trimmedSearch && !isBelowThreshold) {
      return <SkeletonList row={skeletonRow} count={3} />;
    }
    return <ActivityIndicatorLoading />;
  };

  return (
    <Box flex={1} accessibilityLabel={lists[0].title + ' Search results'}>
      <SectionList
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps='always'
        keyboardDismissMode='on-drag'
        sections={lists.map((list, index) => ({
          ...list,
          data: loading ? [] : list.data,
          index,
        }))}
        keyExtractor={(item, index) => {
          const r = item.record as
            | { id?: string; __typename?: string }
            | undefined;
          return `${r?.__typename ?? 'item'}-${r?.id ?? index}`;
        }}
        renderItem={({ item, index, section: { render } }) =>
          render(item, index)
        }
        ItemSeparatorComponent={() => <></>}
        renderSectionFooter={({ section: { sectionFooter } }) =>
          loading ? null : sectionFooter || <Box mb='s' />
        }
        renderSectionHeader={renderHeader}
        ListFooterComponent={renderFooter()}
        onEndReached={onEndReached}
        onEndReachedThreshold={0.5}
      />
    </Box>
  );
};

export default GlobalSearchList;
